"use client";
import ShowcaseInlineCard from "~/components/ShowcaseInlineCard";
import { reasons } from "./config";
import Img from "~/components/UI/Img";
import useIsIntersecting from "~/lib/hooks/is-intersecting";
import { useRef } from "react";
import { cn } from "~/lib/utils";
import type { ShowcaseCardProps } from "~/components/ShowcaseInlineCard/types";

function AnimatedShowcaseInlineCard(
  props: ShowcaseCardProps & {
    index: number;
  },
) {
  const ref = useRef<HTMLDivElement>(null);

  const isIntersecting = useIsIntersecting({
    ref,
    intersectionObserverInit: {
      threshold: 0,
    },
  });

  return (
    <ShowcaseInlineCard
      {...props}
      ref={ref}
      style={{
        transform: !isIntersecting ? "translate(-50%, -50%)" : undefined,
        opacity: isIntersecting ? 1 : 0,
        transitionDelay: `${props.index * 0.1}s`,
        transformOrigin: "center",
        transformStyle: "preserve-3d",
        transition: "transform 0.5s, opacity 0.2s",
      }}
    />
  );
}

export default function WhyInvestInRealEstate() {
  const headRef = useRef<HTMLHeadingElement>(null);
  const ImgRef = useRef<HTMLImageElement>(null);

  const isHeaderIntersecting = useIsIntersecting({
    ref: headRef,
    intersectionObserverInit: {
      threshold: 0.25,
    },
  });
  const isImgIntersecting = useIsIntersecting({
    ref: ImgRef,
    intersectionObserverInit: {
      threshold: 0,
    },
  });

  return (
    <section
      className="flex max-w-[1720px] mx-auto px-8 py-16 flex-col sm:flex-row md:pt-32 gap-x-16 gap-y-12 max-sm:text-center overflow-hidden"
      id="why-invest-in-real-estate"
    >
      <div className="flex flex-col gap-y-4 text-4xl.5 max-sm:items-center">
        <header>
          <h2
            className={cn(
              "font-minion-pro sm:ps-10 leading-tight",
              "transition-all duration-700 ease-in-out delay-300",
              !isHeaderIntersecting
                ? "-translate-y-1/2 -translate-x-1/2 opacity-0"
                : "opacity-100",
            )}
            ref={headRef}
          >
            Why invest in <span className="text-secondary">Real Estate?</span>
          </h2>
        </header>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {reasons.map((reason, index) => (
            <AnimatedShowcaseInlineCard
              key={reason.title}
              {...reason}
              size="sm"
              className="max-w-[300px]"
              index={index}
            />
          ))}
        </div>
      </div>

      <figure className="flex justify-center">
        <Img
          src="/images/Screenshot_from_2024-06-28_15-44-24-removebg-preview.png"
          width={408}
          height={500}
          className={cn(
            "object-contain",
            "transition-all duration-700 ease-in-out delay-300",
            !isImgIntersecting
              ? "translate-y-1/2 translate-x-1/2 opacity-0"
              : "opacity-100",
          )}
          ref={ImgRef}
        />
      </figure>
    </section>
  );
}
